/* ==========================================================================
   UnoKeeper Academy — academy.js  (ES module)
   Depende de globals cargados por <script defer> antes de este módulo:
   window.gsap, window.ScrollTrigger, window.SplitText, window.Lenis.
   Degrada con elegancia si falta cualquiera de ellos o si el usuario
   pidió prefers-reduced-motion.
   ========================================================================== */

const D = document;
const html = D.documentElement;
const reduced = html.classList.contains('reduced-motion');

const gsap = window.gsap || null;
const ScrollTrigger = window.ScrollTrigger || null;
const SplitText = window.SplitText || null;
const Lenis = window.Lenis || null;
const hasGsap = !!(gsap && ScrollTrigger);

let lenis = null;

/* ---------- utilidades ---------------------------------------------------- */
const $  = (sel, ctx = D) => ctx.querySelector(sel);
const $$ = (sel, ctx = D) => Array.from(ctx.querySelectorAll(sel));
const pad2 = (n) => String(n).padStart(2, '0');

const DOW_SHORT = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
const MONTH_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const fmtLong = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/* Fallback total: deja todo visible (sin animación). */
function revealAll() {
  $$('[data-reveal]').forEach((el) => { el.style.opacity = '1'; el.style.transform = 'none'; });
  $$('.uk-hero__line > span').forEach((el) => { el.style.transform = 'none'; });
}

/* ---------- smooth scroll (Lenis) ---------------------------------------- */
function initSmoothScroll() {
  if (reduced || !Lenis) return;
  lenis = new Lenis({
    duration: 0.6,       // 1.05 se sentía "lenta" con rueda de mouse (feedback Paulo 2026-09-01)
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    smoothTouch: false   // nunca en touch: destruye el feel nativo
  });
  if (hasGsap) {
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  } else {
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
  }
}

/* ---------- navegación por anclas ---------------------------------------- */
function scrollToTarget(target) {
  if (!target) return;
  if (lenis) lenis.scrollTo(target, { offset: -64 });
  else target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
}
function initAnchors() {
  $$('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const id = a.getAttribute('href');
      if (id === '#' || id.length < 2) return;
      const target = D.querySelector(id);
      if (!target) return;
      e.preventDefault();
      if (a.hasAttribute('data-aviso-link')) {
        // abre el <details> destino (aviso de privacidad o carta responsiva)
        const det = target.tagName === 'DETAILS' ? target : D.getElementById('aviso-privacidad');
        if (det) det.open = true;
      }
      scrollToTarget(target);
      history.replaceState(null, '', id);
    });
  });
}

/* ---------- reveal on scroll ---------------------------------------------- */
function initReveal() {
  if (reduced || !hasGsap) { revealAll(); return; }
  gsap.set('[data-reveal]', { opacity: 0, y: 48 });
  ScrollTrigger.batch('[data-reveal]', {
    start: 'top 86%',
    onEnter: (batch) => gsap.to(batch, {
      opacity: 1, y: 0, duration: 0.9, stagger: 0.12, ease: 'power2.out', overwrite: true
    })
  });
}

/* ---------- parallax del hero (media se desliza con el scroll) ------------ */
function initHeroParallax() {
  if (reduced || !hasGsap) return;
  const media = $('[data-uk-hero-media]');
  const hero = $('.uk-hero');
  if (!media || !hero) return;
  media.classList.add('is-parallax'); // apaga el zoom CSS (pelearían por transform)
  gsap.fromTo(media,
    { yPercent: -6, scale: 1.14 },
    {
      yPercent: 10, scale: 1.14, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6 }
    });
}

/* ---------- hero split reveal --------------------------------------------- */
function initHero() {
  const spans = $$('.uk-hero__line > span');
  if (reduced || !hasGsap || !SplitText) {
    spans.forEach((s) => { s.style.animation = 'none'; s.style.transform = 'none'; });
    return;
  }
  const tl = gsap.timeline({ delay: 0.15 });
  spans.forEach((span) => {
    // apaga la red de seguridad CSS (uk-line-in) antes de que GSAP tome el control
    span.style.animation = 'none';
    span.style.transform = 'none';
    const split = new SplitText(span, { type: 'chars' });
    tl.from(split.chars, {
      yPercent: 115, opacity: 0, duration: 1.0, stagger: 0.04, ease: 'power3.out'
    }, '<0.12');
  });
}

/* ---------- contadores de stats ------------------------------------------- */
function initCounters() {
  const nodes = $$('[data-uk-counter]');
  if (!nodes.length) return;
  const setFinal = (el) => { el.textContent = el.getAttribute('data-uk-counter'); };
  if (reduced || !hasGsap) { nodes.forEach(setFinal); return; }
  nodes.forEach((el) => {
    const target = parseInt(el.getAttribute('data-uk-counter'), 10);
    if (isNaN(target)) { setFinal(el); return; }
    const obj = { v: 0 };
    ScrollTrigger.create({
      trigger: el, start: 'top 88%', once: true,
      onEnter: () => gsap.to(obj, {
        v: target, duration: 1.6, ease: 'power2.out',
        onUpdate: () => { el.textContent = String(Math.round(obj.v)); }
      })
    });
  });
}

/* ---------- header blur + sticky CTA (un solo handler) ------------------- */
function initScrollUI() {
  const header = $('[data-uk-header]');
  const cta = $('[data-uk-sticky-cta]');
  const agendar = D.getElementById('agendar');
  let agendarVisible = false;
  let ticking = false;

  if (agendar && cta) {
    new IntersectionObserver((entries) => {
      agendarVisible = entries[0].isIntersecting;
      apply();
    }, { threshold: 0 }).observe(agendar);
  }
  function apply() {
    const y = window.scrollY || window.pageYOffset;
    if (header) header.classList.toggle('is-scrolled', y > 40);
    if (cta) cta.classList.toggle('is-visible', y > window.innerHeight * 0.3 && !agendarVisible);
    ticking = false;
  }
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(apply);
  }, { passive: true });
  apply();
}

/* ---------- timeline draw ------------------------------------------------- */
function initTimeline() {
  if (reduced || !hasGsap) return;
  const tl = $('[data-uk-timeline]');
  const fill = $('[data-uk-timeline-fill]');
  if (!tl || !fill) return;
  gsap.to(fill, {
    scaleY: 1, ease: 'none',
    scrollTrigger: { trigger: tl, start: 'top 72%', end: 'bottom 82%', scrub: true }
  });
}

/* ---------- parallax galería ---------------------------------------------- */
function initParallax() {
  if (reduced || !hasGsap) return;
  $$('[data-uk-parallax]').forEach((img, i) => {
    const amt = -8 - (i % 4) * 3; // -8 .. -17
    gsap.fromTo(img,
      { yPercent: -amt },
      {
        yPercent: amt, ease: 'none',
        scrollTrigger: {
          trigger: img.closest('.uk-mosaic__cell') || img,
          start: 'top bottom', end: 'bottom top', scrub: 1
        }
      });
  });
}

/* ---------- magnetic CTAs (solo pointer fino) ------------------------------ */
function initMagnetic() {
  if (reduced || !hasGsap) return;
  if (!window.matchMedia('(pointer: fine)').matches) return;
  $$('[data-uk-magnetic]').forEach((btn) => {
    btn.addEventListener('mousemove', (e) => {
      const r = btn.getBoundingClientRect();
      const x = e.clientX - (r.left + r.width / 2);
      const y = e.clientY - (r.top + r.height / 2);
      gsap.to(btn, { x: x / 4, y: y / 4, duration: 0.4, ease: 'power3.out' });
    });
    btn.addEventListener('mouseleave', () => {
      gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.45)' });
    });
  });
}

/* ---------- acordeón FAQ (slide-down, sin display:none) ----------------- */
function initAccordion() {
  const root = $('[data-uk-accordion]');
  if (!root) return;
  $$('details.uk-faq-item', root).forEach((det) => {
    const summary = $('summary', det);
    const panel = $('.uk-faq-item__a', det);
    if (!summary || !panel) return;
    summary.addEventListener('click', (e) => {
      if (reduced || !hasGsap) return; // comportamiento nativo
      e.preventDefault();
      if (det.open) {
        gsap.to(panel, {
          height: 0, duration: 0.32, ease: 'power2.inOut',
          onComplete: () => { det.open = false; panel.style.height = ''; }
        });
      } else {
        det.open = true;
        gsap.fromTo(panel, { height: 0 }, {
          height: panel.scrollHeight, duration: 0.38, ease: 'power2.out',
          onComplete: () => { panel.style.height = ''; }
        });
      }
    });
  });
}

/* ---------- sliders (testimonios y coaches) ------------------------------ */
function initSlider() {
  $$('[data-uk-slider]').forEach((slider) => {
    const track = $('[data-slider-track]', slider);
    if (!track) return;
    const step = () => {
      const card = track.firstElementChild;
      if (!card) return 320;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return card.offsetWidth + gap;
    };
    const prev = $('[data-slider-prev]', slider);
    const next = $('[data-slider-next]', slider);
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

    /* Arrastre con el mouse, como el dedo en móvil (en touch el scroll nativo
       ya lo hace). Durante el drag se apaga el snap (.is-dragging) y al soltar
       se re-encaja a la tarjeta más cercana conservando la fase del snap. */
    let dragPid = null, downX = 0, startLeft = 0, dragMoved = false;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragPid = e.pointerId; downX = e.clientX; startLeft = track.scrollLeft; dragMoved = false;
    });
    track.addEventListener('pointermove', (e) => {
      if (e.pointerId !== dragPid) return;
      const dx = e.clientX - downX;
      if (!dragMoved && Math.abs(dx) > 5) {
        dragMoved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(dragPid); } catch (_) { /* noop */ }
      }
      if (dragMoved) track.scrollLeft = startLeft - dx;
    });
    const endDrag = (e) => {
      if (e.pointerId !== dragPid) return;
      dragPid = null;
      if (!dragMoved) return;
      const s = step();
      const max = track.scrollWidth - track.clientWidth;
      let target = startLeft + Math.round((track.scrollLeft - startLeft) / s) * s;
      target = Math.max(0, Math.min(target, max));
      track.scrollTo({ left: target, behavior: 'smooth' });
      setTimeout(() => track.classList.remove('is-dragging'), 400);
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('dragstart', (e) => e.preventDefault());
    track.addEventListener('click', (e) => {
      if (dragMoved) { e.preventDefault(); e.stopPropagation(); dragMoved = false; }
    }, true);
  });
}

/* ---------- lightbox de galería (vanilla) ------------------------------- */
function initLightbox() {
  const root = $('[data-uk-lightbox-root]');
  if (!root) return;
  const triggers = $$('[data-uk-lightbox]');
  if (!triggers.length) return;
  const img = $('[data-lb-img]', root);
  const srcs = triggers.map((t) => t.getAttribute('data-uk-lightbox'));
  let idx = 0;
  let lastFocus = null;

  function show(i) {
    idx = (i + srcs.length) % srcs.length;
    img.src = srcs[idx];
  }
  function open(i) {
    lastFocus = D.activeElement;
    show(i);
    root.hidden = false;
    requestAnimationFrame(() => root.classList.add('is-open'));
    if (lenis) lenis.stop();
    D.body.style.overflow = 'hidden';
    $('[data-lb-close]', root).focus();
  }
  function close() {
    root.classList.remove('is-open');
    if (lenis) lenis.start();
    D.body.style.overflow = '';
    setTimeout(() => { root.hidden = true; img.src = ''; }, 300);
    if (lastFocus) lastFocus.focus();
  }

  triggers.forEach((t, i) => t.addEventListener('click', () => open(i)));
  $('[data-lb-close]', root).addEventListener('click', close);
  $('[data-lb-prev]', root).addEventListener('click', () => show(idx - 1));
  $('[data-lb-next]', root).addEventListener('click', () => show(idx + 1));
  root.addEventListener('click', (e) => { if (e.target === root) close(); });
  D.addEventListener('keydown', (e) => {
    if (root.hidden) return;
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') show(idx - 1);
    if (e.key === 'ArrowRight') show(idx + 1);
  });
  // swipe en touch
  let sx = 0;
  root.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
  root.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - sx;
    if (Math.abs(dx) > 50) show(dx > 0 ? idx - 1 : idx + 1);
  }, { passive: true });
}

/* ==========================================================================
   AGENDADOR — day picker + formulario
   ========================================================================== */

/* Estado compartido entre day picker y formulario. */
const booking = {
  selected: [],    // [{iso:'2026-06-16', time:'17:00', label:'5:00 – 6:00 PM', dur:60}]
  ready: false,    // true cuando hay exactamente 2 clases elegidas
  conflict: false, // true si alguna clase elegida no corresponde a la edad capturada
  conflictMsg: ''  // por qué no corresponde (se muestra al intentar enviar)
};

/* Escape mínimo: los textos de grupo vienen de los ajustes del tema, pero el
   botón se arma con innerHTML — no metemos markup ajeno al DOM. */
function esc(v) {
  return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

/* "de 12 a 14 años" / "hasta 14 años" / "de 12 años en adelante" / "" */
function ageRangeText(min, max) {
  if (min > 0 && max > 0) return 'de ' + min + ' a ' + max + ' años';
  if (max > 0) return 'hasta ' + max + ' años';
  if (min > 0) return 'de ' + min + ' años en adelante';
  return '';
}

/* ---------- day picker ---------------------------------------------------- */
function initDayPicker(form, onChange) {
  const wrap = $('[data-uk-daypicker]');
  if (!wrap || !form) return;
  const emptyMsg = $('[data-daypicker-empty]', wrap);
  const status = $('[data-daypicker-status]');
  const countEl = $('[data-daypicker-count]');
  const errEl = $('[data-daypicker-err]');
  const ageNote = $('[data-daypicker-agenote]');
  const input1 = $('[data-clase-1]', form);
  const input2 = $('[data-clase-2]', form);

  let slots = [];
  try {
    const node = $('script[data-uk-slots]');
    if (node) slots = JSON.parse(node.textContent) || [];
  } catch (e) { slots = []; }
  slots = slots.filter((s) => s && typeof s.w === 'number' && s.t);

  if (!slots.length) {
    if (emptyMsg) emptyMsg.hidden = false;
    if (status) status.hidden = true;
    return null;
  }

  const lead = Math.max(0, parseInt(form.dataset.leadDays, 10) || 1);
  const windowDays = Math.max(7, parseInt(form.dataset.windowDays, 10) || 21);

  // Genera las fechas disponibles: día por día dentro de la ventana,
  // un botón por cada horario que caiga en ese día de la semana.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const options = [];
  for (let d = lead; d <= lead + windowDays; d++) {
    const date = new Date(today);
    date.setDate(today.getDate() + d);
    slots.forEach((slot) => {
      if (slot.w === date.getDay()) {
        options.push({
          iso: date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate()),
          dow: DOW_SHORT[date.getDay()],
          num: date.getDate(),
          month: MONTH_SHORT[date.getMonth()],
          time: slot.t,
          label: slot.label || slot.t,
          dur: slot.dur || 60,
          group: slot.g || '',
          groupShort: slot.gs || '',
          min: parseInt(slot.min, 10) || 0,
          max: parseInt(slot.max, 10) || 0,
          dateObj: new Date(date)
        });
      }
    });
  }

  if (!options.length) {
    if (emptyMsg) emptyMsg.hidden = false;
    return null;
  }

  function syncState() {
    booking.selected.sort((a, b) => (a.iso + a.time).localeCompare(b.iso + b.time));
    booking.ready = booking.selected.length === 2;
    if (input1) input1.value = booking.selected[0] ? booking.selected[0].iso + ' ' + booking.selected[0].time : '';
    if (input2) input2.value = booking.selected[1] ? booking.selected[1].iso + ' ' + booking.selected[1].time : '';
    if (countEl) countEl.textContent = String(booking.selected.length);
    if (status) status.classList.toggle('is-complete', booking.ready);
    if (errEl && booking.selected.length) errEl.textContent = '';
    if (onChange) onChange();
  }

  /* -- filtro por edad: cada clase puede pertenecer a un grupo con rango de edad -- */
  const entries = [];        // [{ opt, btn, blocked }]
  let currentAge = null;     // null = todavía no sabemos la edad -> no se filtra nada

  function blockedFor(opt, age) {
    if (age == null) return false;
    if (opt.min > 0 && age < opt.min) return true;
    if (opt.max > 0 && age > opt.max) return true;
    return false;
  }
  /* La clase del MISMO día que sí le corresponde a esa edad. Es la salida natural:
     el papá ya eligió ese día porque le acomoda — no hay que mandarlo al calendario
     otra vez, hay que ofrecerle el otro horario de esa misma fecha. */
  function sameDayFit(opt, age) {
    return entries
      .map((e) => e.opt)
      .find((o) => o.iso === opt.iso && o.time !== opt.time && !blockedFor(o, age)) || null;
  }
  function reasonFor(opt, age) {
    const range = ageRangeText(opt.min, opt.max);
    const who = opt.group ? 'del grupo ' + opt.group.toLowerCase() : 'de otro grupo';
    const base = 'La clase de ' + opt.label + ' es ' + who + (range ? ' (' + range + ')' : '');
    const alt = sameDayFit(opt, age);
    if (alt) {
      return base + '. Con ' + age + ' años le toca la de ' + alt.label +
        ', ese mismo ' + fmtLong.format(alt.dateObj) + '.';
    }
    return base + '. Con ' + age + ' años elige otro horario.';
  }
  function ariaFor(opt, blocked, conflict) {
    return cap(fmtLong.format(opt.dateObj)) + ', ' + opt.label +
      (opt.group ? ', grupo ' + opt.group : '') +
      (conflict ? ' — elegida, pero no corresponde a la edad capturada: actívala para quitarla'
        : blocked ? ' — no disponible para la edad capturada' : '');
  }
  function isSelected(opt) {
    return booking.selected.some((s) => s.iso + s.time === opt.iso + opt.time);
  }
  /* `action` = {label, onClick}: además de explicar, ofrece el cambio de un toque.
     IDEMPOTENTE a propósito: si el aviso no cambió, NO se vuelve a pintar. Sin
     esto el botón se perdía el clic — al tocarlo, el campo de edad soltaba el
     foco, eso disparaba `change` → applyAge → repintado, y el nodo que había
     recibido el mousedown ya no existía para el mouseup: el navegador nunca
     emitía el `click`. (Detectado en Playwright: el clic sintético por JS
     funcionaba y el de ratón no.) */
  let noteSig = null;
  function setAgeNote(msg, tone, action) {
    if (!ageNote) return;
    const sig = (msg || '') + '|' + (tone || '') + '|' + (action ? action.label : '');
    if (sig === noteSig) return;          // mismo aviso: el DOM se queda quieto
    noteSig = sig;
    ageNote.textContent = '';
    ageNote.hidden = !msg;
    ageNote.classList.toggle('is-warn', tone === 'warn');
    if (!msg) return;
    ageNote.appendChild(D.createTextNode(msg));
    if (!action) return;
    const b = D.createElement('button');
    b.type = 'button';
    b.className = 'uk-agenote__swap';
    b.textContent = action.label;
    b.addEventListener('click', action.onClick);
    ageNote.appendChild(b);
  }

  /* Cambia la clase que no corresponde por la del mismo día que sí. */
  function swapTo(badOpt, goodOpt) {
    const i = booking.selected.findIndex((s) => s.iso + s.time === badOpt.iso + badOpt.time);
    if (i >= 0) booking.selected.splice(i, 1);
    const badBtn = wrap.querySelector('[data-key="' + badOpt.iso + badOpt.time + '"]');
    if (badBtn) { badBtn.classList.remove('is-selected', 'is-conflict'); badBtn.setAttribute('aria-pressed', 'false'); }
    if (!isSelected(goodOpt)) {
      booking.selected.push({
        iso: goodOpt.iso, time: goodOpt.time, label: goodOpt.label,
        dur: goodOpt.dur, group: goodOpt.group, dateObj: goodOpt.dateObj
      });
      const goodBtn = wrap.querySelector('[data-key="' + goodOpt.iso + goodOpt.time + '"]');
      if (goodBtn) {
        goodBtn.classList.add('is-selected');
        goodBtn.setAttribute('aria-pressed', 'true');
        if (hasGsap && !reduced) gsap.fromTo(goodBtn, { scale: 0.92 }, { scale: 1.04, duration: 0.35, ease: 'back.out(2.5)' });
      }
    }
    syncState();
    applyAge(currentAge);
  }

  /* Recalcula qué botones quedan activos para `age`.
     NUNCA borra una selección por su cuenta: si una clase ya elegida deja de
     corresponder, la marca en conflicto, explica por qué y bloquea el envío —
     el usuario la quita tocándola. Así nada desaparece a media captura
     (escribir "6" camino a "60" no debe tirarle la selección a nadie). */
  function applyAge(age) {
    currentAge = (typeof age === 'number' && isFinite(age)) ? age : null;
    let available = 0;
    const conflicts = [];

    entries.forEach((e) => {
      const blocked = blockedFor(e.opt, currentAge);
      // Una clase ya elegida que deja de corresponder NO se marca aria-disabled:
      // sigue siendo accionable — es justo la que el usuario tiene que quitar.
      const conflict = blocked && isSelected(e.opt);
      e.blocked = blocked;
      if (!blocked) available += 1;
      e.btn.classList.toggle('is-locked', blocked);
      e.btn.classList.toggle('is-conflict', conflict);
      e.btn.setAttribute('aria-disabled', (blocked && !conflict) ? 'true' : 'false');
      e.btn.setAttribute('aria-label', ariaFor(e.opt, blocked, conflict));
      if (conflict) conflicts.push(e);
    });

    booking.conflict = conflicts.length > 0;
    // "2 de 2 clases elegidas" en verde no puede convivir con un error rojo debajo.
    if (status) status.classList.toggle('is-complete', booking.ready && !booking.conflict);
    const alt = conflicts.length ? sameDayFit(conflicts[0].opt, currentAge) : null;
    booking.conflictMsg = conflicts.length
      ? reasonFor(conflicts[0].opt, currentAge) +
        (alt ? '' : ' Tócala para quitarla y elige otra fecha.')
      : '';

    if (conflicts.length) {
      setAgeNote('⚠️ ' + booking.conflictMsg, 'warn',
        alt ? { label: 'Cambiar a la de ' + alt.label,
                onClick: function () { swapTo(conflicts[0].opt, alt); } } : null);
    }
    else if (currentAge != null && available === 0) {
      setAgeNote('⚠️ Con ' + currentAge + ' años no hay grupo disponible en el calendario. Escríbenos por WhatsApp y te ubicamos.', 'warn');
    } else if (currentAge != null && entries.some((e) => e.blocked)) {
      setAgeNote('Dejamos activas solo las clases del grupo que le toca a un portero de ' + currentAge + ' años.');
    } else {
      setAgeNote('');
    }

    if (onChange) onChange();
  }

  options.forEach((opt) => {
    const btn = D.createElement('button');
    btn.type = 'button';
    btn.className = 'uk-day';
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', ariaFor(opt, false, false));
    btn.innerHTML =
      '<span class="uk-day__dow">' + opt.dow + '</span>' +
      '<span class="uk-day__num">' + opt.num + '</span>' +
      '<span class="uk-day__month">' + opt.month + '</span>' +
      '<span class="uk-day__time">' + esc(opt.label.replace(/\s/g, '')) + '</span>' +
      (opt.groupShort ? '<span class="uk-day__group">' + esc(opt.groupShort) + '</span>' : '');

    btn.addEventListener('click', () => {
      const key = opt.iso + opt.time;
      const i = booking.selected.findIndex((s) => s.iso + s.time === key);
      if (i >= 0) {
        booking.selected.splice(i, 1);
        btn.classList.remove('is-selected', 'is-conflict');
        btn.setAttribute('aria-pressed', 'false');
      } else {
        // Clase de otro grupo de edad: no se puede elegir, pero sí explicamos por qué.
        if (blockedFor(opt, currentAge)) {
          const fit = sameDayFit(opt, currentAge);
          setAgeNote('⚠️ ' + reasonFor(opt, currentAge), 'warn',
            fit ? { label: 'Elegir la de ' + fit.label,
                    onClick: function () { swapTo(opt, fit); } } : null);
          if (hasGsap && !reduced) gsap.fromTo(btn, { x: -4 }, { x: 0, duration: 0.4, ease: 'elastic.out(1,0.35)' });
          return;
        }
        if (booking.selected.length >= 2) {
          // Reemplaza la selección más antigua para no frustrar al usuario.
          const removed = booking.selected.shift();
          const prevBtn = wrap.querySelector('[data-key="' + removed.iso + removed.time + '"]');
          if (prevBtn) { prevBtn.classList.remove('is-selected', 'is-conflict'); prevBtn.setAttribute('aria-pressed', 'false'); }
        }
        booking.selected.push({
          iso: opt.iso, time: opt.time, label: opt.label, dur: opt.dur,
          group: opt.group, dateObj: opt.dateObj
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        if (hasGsap && !reduced) {
          gsap.fromTo(btn, { scale: 0.92 }, { scale: 1.04, duration: 0.35, ease: 'back.out(2.5)' });
        }
      }
      syncState();            // inputs ocultos + contador
      applyAge(currentAge);   // recalcula conflictos y refresca el gate del submit
    });

    btn.setAttribute('data-key', opt.iso + opt.time);
    wrap.appendChild(btn);
    entries.push({ opt: opt, btn: btn, blocked: false });
  });

  syncState();
  return { applyAge: applyAge };
}

/* ---------- formulario ---------------------------------------------------- */
function initForm() {
  const form = D.getElementById('uk-academy-form');
  if (!form) return;
  const successBox = $('[data-form-success]');
  const errorBox = $('[data-form-error]', form);
  const submitBtn = $('[data-form-submit]', form);
  const tutorField = $('[data-tutor-field]', form);
  const tutorInput = form.elements['tutorNombre'];
  const edad = form.elements['edad'];
  const dayErr = $('[data-daypicker-err]');

  /* -- mensajes de error en español -- */
  function messageFor(field) {
    const v = field.validity;
    if (v.valueMissing) return field.type === 'checkbox'
      ? 'Debes aceptar para continuar.' : 'Este campo es obligatorio.';
    if (v.typeMismatch && field.type === 'email') return 'Escribe un correo válido.';
    if (v.patternMismatch) return 'Deben ser 10 dígitos, sin espacios ni guiones.';
    if (v.rangeUnderflow) return 'El valor mínimo es ' + field.min + '.';
    if (v.rangeOverflow) return 'El valor máximo es ' + field.max + '.';
    if (v.tooShort) return 'Escribe al menos ' + field.minLength + ' caracteres.';
    return 'Revisa este dato.';
  }
  function fieldError(field) {
    const wrapEl = field.closest('.uk-field');
    return wrapEl ? wrapEl.querySelector('[data-err]') : null;
  }
  function validateField(field) {
    if (field.type === 'hidden' || field.name === 'website') return true;
    const ok = field.validity.valid;
    const err = fieldError(field);
    field.setAttribute('aria-invalid', ok ? 'false' : 'true');
    if (err) err.textContent = ok ? '' : '⚠️ ' + messageFor(field);
    return ok;
  }

  /* Edad utilizable: solo cuando el número está completo y dentro del rango del
     campo. Así, mientras se teclea "6" camino a "60", no filtramos el calendario
     con una edad a medias. */
  function usableAge() {
    const v = parseInt(edad.value, 10);
    if (isNaN(v)) return null;
    const lo = parseInt(edad.min, 10) || 0;
    const hi = parseInt(edad.max, 10) || 200;
    return (v < lo || v > hi) ? null : v;
  }

  /* -- tutor obligatorio si el portero es menor -- */
  function syncTutor() {
    const age = parseInt(edad.value, 10);
    const isMinor = !isNaN(age) && age < 18;
    if (tutorField) tutorField.hidden = !isMinor;
    if (tutorInput) {
      if (isMinor) {
        tutorInput.setAttribute('required', '');
      } else {
        tutorInput.removeAttribute('required');
        tutorInput.removeAttribute('aria-invalid');
        const err = fieldError(tutorInput);
        if (err) err.textContent = '';
      }
    }
    refreshGate();
  }

  /* -- gate del botón submit: campos válidos + 2 clases elegidas + sin choque de grupo -- */
  function refreshGate() {
    submitBtn.disabled = !(form.checkValidity() && booking.ready && !booking.conflict);
  }

  /* -- estados de UI -- */
  function setLoading(on) {
    submitBtn.classList.toggle('is-loading', on);
    submitBtn.disabled = on;
  }
  function showError(msg) {
    errorBox.textContent = '⚠️ ' + msg;
    errorBox.hidden = false;
    scrollToTarget(errorBox);
  }
  function clearError() { errorBox.hidden = true; errorBox.textContent = ''; }

  function classLine(c) {
    return cap(fmtLong.format(c.dateObj || new Date(c.iso + 'T12:00:00'))) + ' · ' + c.label +
      (c.group ? ' · ' + c.group : '');
  }

  function showSuccess(data, viaWhatsApp) {
    form.hidden = true;
    if (!successBox) return;
    const datesEl = successBox.querySelector('[data-success-dates]');
    if (datesEl && booking.selected.length === 2) {
      datesEl.innerHTML = '🧤 ' + classLine(booking.selected[0]) + '<br>🧤 ' + classLine(booking.selected[1]);
    }
    if (data && data.codigo) {
      const codeWrap = successBox.querySelector('[data-success-code-wrap]');
      const codeEl = successBox.querySelector('[data-success-code]');
      if (codeEl) codeEl.textContent = data.codigo;
      if (codeWrap) codeWrap.hidden = false;
    }
    if (viaWhatsApp) {
      const txt = successBox.querySelector('.uk-success__text');
      if (txt) txt.textContent = 'Abrimos WhatsApp con tu solicitud lista — solo dale enviar y te confirmamos por ahí. Llega 10 minutos antes con tu ropa deportiva de portero, tus guantes y tenis multitaco para pasto sintético.';
    }
    successBox.hidden = false;
    scrollToTarget(successBox);
  }

  /* -- payload -- */
  // El grupo viaja DENTRO del label: /api/academia/registro copia solo una lista
  // blanca de claves y descartaría un campo nuevo sin avisar. Así el coach ve el
  // grupo en el Sheet, en el aviso de WhatsApp y en el recordatorio.
  function labelWithGroup(c) {
    if (!c || !c.label) return '';
    return c.group ? c.label + ' · ' + c.group : c.label;
  }

  function buildPayload() {
    const checkbox = (n) => !!(form.elements[n] && form.elements[n].checked);
    const text = (n) => (form.elements[n] ? String(form.elements[n].value).trim() : '');
    const c1 = booking.selected[0] || {};
    const c2 = booking.selected[1] || {};
    return {
      nombre: text('nombre'), apellidos: text('apellidos'), edad: text('edad'),
      experiencia: text('experiencia'), tallaGuantes: text('tallaGuantes'),
      tutorNombre: text('tutorNombre'),
      telefono: text('whatsapp'), whatsapp: text('whatsapp'), email: text('email'),
      comoNosConociste: text('comoNosConociste'),
      clase1Fecha: c1.iso || '', clase1Hora: c1.time || '', clase1Label: labelWithGroup(c1), clase1Dur: c1.dur || 60,
      clase1Grupo: c1.group || '',
      clase2Fecha: c2.iso || '', clase2Hora: c2.time || '', clase2Label: labelWithGroup(c2), clase2Dur: c2.dur || 60,
      clase2Grupo: c2.group || '',
      avisoPrivacidad: checkbox('avisoPrivacidad'),
      cartaResponsiva: checkbox('cartaResponsiva'),
      vericidad: checkbox('vericidad'),
      derechosImagen: checkbox('derechosImagen'),
      comunicacionesComerciales: checkbox('comunicacionesComerciales'),
      // versión de los textos aceptados (cubren clases muestra + permanencia si se inscribe)
      terminosVersion: 'academia-terminos-2026-09-01',
      website: text('website'),
      userAgent: navigator.userAgent
    };
  }

  /* -- fallback: armar el link de WhatsApp con la solicitud completa -- */
  function waUrl() {
    const p = buildPayload();
    const lines = [
      'Hola, quiero agendar mis 2 clases muestra gratis en UnoKeeper Academy 🧤',
      '',
      '*Portero:* ' + p.nombre + ' ' + p.apellidos + ' (' + p.edad + ' años)',
      '*Experiencia:* ' + p.experiencia,
      p.tutorNombre ? '*Tutor:* ' + p.tutorNombre : '',
      '*WhatsApp:* ' + p.whatsapp + ' · *Correo:* ' + p.email,
      '',
      '*Clase 1:* ' + classLine(booking.selected[0]),
      '*Clase 2:* ' + classLine(booking.selected[1]),
      '',
      '(Enviado desde unokeeper.com/pages/academia)'
    ].filter(Boolean);
    const number = form.dataset.waNumber || '524773904522';
    return 'https://wa.me/' + number + '?text=' + encodeURIComponent(lines.join('\n'));
  }
  // Solo confiable DENTRO del gesto del usuario (sin await previo): un window.open
  // tras un await lo bloquea el navegador. Para el path de error usar showFallbackError.
  function whatsappFallback() { window.open(waUrl(), '_blank', 'noopener'); }

  // El registro falló: NO mostramos éxito falso. Damos un link de WhatsApp que el
  // usuario TOCA (gesto real → abre WhatsApp aunque bloquee popups) y dejamos el
  // form visible para reintentar. `encodeURIComponent` deja el href seguro para HTML.
  function showFallbackError() {
    errorBox.innerHTML = '⚠️ No pudimos guardar tu registro en este momento. ' +
      'Toca aquí para enviarlo por WhatsApp y te confirmamos: ' +
      '<a href="' + waUrl() + '" target="_blank" rel="noopener">Enviar por WhatsApp</a>. ' +
      'O vuelve a intentar en un momento.';
    errorBox.hidden = false;
    scrollToTarget(errorBox);
  }

  /* -- listeners -- */
  $$('input, select', form).forEach((field) => {
    field.addEventListener('blur', () => validateField(field));
    field.addEventListener('input', refreshGate);
    field.addEventListener('change', refreshGate);
  });
  /* day picker se inicializa con un callback que refresca el gate */
  const dayPicker = initDayPicker(form, refreshGate);

  function syncAge() {
    syncTutor();
    if (dayPicker) dayPicker.applyAge(usableAge());
  }
  edad.addEventListener('input', syncAge);
  edad.addEventListener('change', syncAge);
  syncAge();

  /* -- submit -- */
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearError();

    // Honeypot: bot detectado -> éxito falso silencioso, no se envía nada.
    if (form.elements['website'] && form.elements['website'].value.trim() !== '') {
      showSuccess();
      return;
    }
    if (!booking.ready) {
      if (dayErr) dayErr.textContent = '⚠️ Elige tus 2 clases en el calendario de arriba.';
      scrollToTarget($('[data-uk-daypicker]'));
      return;
    }
    // Una de las clases elegidas no corresponde al grupo de edad del portero.
    if (booking.conflict) {
      if (dayErr) dayErr.textContent = '⚠️ ' + booking.conflictMsg;
      scrollToTarget($('[data-uk-daypicker]'));
      return;
    }
    // Validación completa con errores inline.
    if (!form.checkValidity()) {
      $$('input, select', form).forEach(validateField);
      const firstBad = form.querySelector('[aria-invalid="true"]');
      if (firstBad) { firstBad.focus(); scrollToTarget(firstBad.closest('.uk-field') || firstBad); }
      return;
    }

    const endpoint = form.dataset.endpoint;
    if (!endpoint) {
      // Sin Apps Script conectado: la solicitud viaja por WhatsApp.
      whatsappFallback();
      showSuccess(null, true);
      return;
    }
    setLoading(true);
    // Timeout duro: si el VPS acepta la conexión pero nunca responde, no dejamos
    // el botón girando para siempre — abortamos a los 15s y caemos al fallback.
    const ctrl = new AbortController();
    const timer = setTimeout(function () { ctrl.abort(); }, 15000);
    try {
      // Apps Script no soporta preflight CORS → text/plain; Make sí, y solo parsea el JSON
      // del body si el Content-Type es application/json.
      const isAppsScript = endpoint.indexOf('script.google') !== -1;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': isAppsScript ? 'text/plain;charset=utf-8' : 'application/json' },
        body: JSON.stringify(buildPayload()),
        signal: ctrl.signal
      });
      const data = await res.json();
      if (!res.ok || !data || data.status !== 'success') {
        throw new Error((data && data.message) || 'No pudimos guardar tu agendado.');
      }
      showSuccess(data);
    } catch (err) {
      // El registro NO se guardó (429/502/timeout/JSON inválido). NO mostramos éxito
      // falso: enseñamos el error con un link de WhatsApp tappable y dejamos el form.
      showFallbackError();
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  });

  // estado inicial
  syncTutor();
  refreshGate();
}

/* ---------- videos diferidos: fondo del hero y banner suelto -------------- */
/* Ninguno se descarga con la página: la fuente se asigna tras el load + idle,
   así el póster/imagen pinta primero y el video no compite por ancho de banda.
   El códec se elige por canPlayType (AV1 ~31% más liviano cuando el navegador
   lo decodifica) y NO por el atributo `media` de <source>: Safari lo ignora. */
function initLazyVideos() {
  $$('[data-uk-lazyvideo]').forEach(setupLazyVideo);
}

function setupLazyVideo(v) {
  if (!v) return;

  const AV1_TYPE = 'video/mp4; codecs="av01.0.05M.08"';
  const isMobile = () => window.matchMedia('(max-width: 767px)').matches;
  const canAv1 = () => {
    try { return v.canPlayType(AV1_TYPE) === 'probably'; } catch (e) { return false; }
  };
  const pickSrc = () => {
    const m = isMobile();
    const av1 = v.getAttribute(m ? 'data-av1-mobile' : 'data-av1-desktop');
    const h264 = v.getAttribute(m ? 'data-h264-mobile' : 'data-h264-desktop');
    return (av1 && canAv1()) ? av1 : h264;
  };

  let loaded = false;
  const play = () => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
  v.addEventListener('playing', () => v.classList.add('is-ready'), { once: true });
  const load = () => {
    if (loaded) return;
    const src = pickSrc();
    if (!src) return;
    loaded = true;
    v.src = src;
    v.load();
    play();
  };

  // Con prefers-reduced-motion se queda el póster: cero bytes de video.
  if (reduced) return;

  if (!('IntersectionObserver' in window)) { load(); return; }

  // Con el banner arriba del pliegue el observer dispara de inmediato, así que
  // la descarga se engancha al load + idle: primero pinta el póster y cargan los
  // recursos críticos del hero, y sólo después entra el video.
  const whenIdle = (fn) => {
    const run = () => (window.requestIdleCallback
      ? window.requestIdleCallback(fn, { timeout: 1200 })
      : setTimeout(fn, 200));
    if (D.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  };

  // 1) Dispara la descarga poco antes de entrar a pantalla.
  new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => { if (e.isIntersecting) { whenIdle(load); obs.disconnect(); } });
  }, { rootMargin: '400px 0px' }).observe(v);

  // 2) Pausa fuera de vista: no gasta CPU ni batería mientras no se ve.
  new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!loaded) return;
      if (e.isIntersecting) play(); else v.pause();
    });
  }, { threshold: 0 }).observe(v);
}

/* ---------- arranque ----------------------------------------------------- */
function boot() {
  // Bloque de motion: si algo falla, deja todo visible.
  try {
    initSmoothScroll();
    initReveal();
    initHero();
    initHeroParallax();
    initCounters();
    initTimeline();
    initParallax();
    initMagnetic();
  } catch (err) {
    console.warn('[academy] motion init falló, fallback visible:', err);
    revealAll();
  }
  // Bloque funcional: independiente de GSAP.
  try { initScrollUI(); }   catch (e) { console.warn('[academy] scroll UI:', e); }
  try { initAnchors(); }    catch (e) { console.warn('[academy] anchors:', e); }
  try { initAccordion(); }  catch (e) { console.warn('[academy] accordion:', e); }
  try { initSlider(); }     catch (e) { console.warn('[academy] slider:', e); }
  try { initLightbox(); }   catch (e) { console.warn('[academy] lightbox:', e); }
  try { initLazyVideos(); } catch (e) { console.warn('[academy] videos diferidos:', e); }
  try { initForm(); }       catch (e) { console.warn('[academy] form:', e); }

  if (hasGsap) {
    window.addEventListener('load', () => ScrollTrigger.refresh());
  }
}

if (D.readyState === 'loading') {
  D.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
